/**
 * OnboardingStateProvider — org-wide Atlas onboarding state with personal overlay.
 *
 * Replaces the per-browser `localStorage` source of truth from
 * `src/shared/onboarding/onboardingState.ts` with a server-backed snapshot
 * that every member of the organization sees. Personal overlays (Atlas
 * "Restart mission") live in `user_onboarding_override`.
 *
 * Lifecycle:
 *   1. On mount (and whenever orgId/productId changes) we GET
 *      `/api/onboarding-state` to load the three slices: org, orgProduct,
 *      userOverride. While that request is in-flight the provider returns
 *      a best-effort view built from `localStorage` so the first paint
 *      doesn't flash an empty state.
 *   2. After the fetch resolves we subscribe to Supabase Realtime so that
 *      progress made by one teammate is mirrored to every other open tab
 *      within the same organisation within ~1s. Set
 *      `REACT_APP_DISABLE_REALTIME=1` to skip the subscription and rely on
 *      the window-focus re-fetch fallback.
 *   3. All writes go through `patchOrg`, `patchUser`, `clearUserOverride`,
 *      which optimistically update the in-memory snapshot AND trigger the
 *      same `relix:atlasMissionChange` DOM event the existing
 *      AtlasProvider already subscribes to.
 *   4. On the first successful GET (when orgProduct state is empty) we
 *      "bootstrap" by mirroring this user's legacy `relix.onboarding.*`
 *      + `relix.coach.*` localStorage keys up to the server so nobody
 *      loses their in-progress tour.
 *
 * Boolean flags (checkpoints, coach dismissals) are monotonic — once true
 * they stay true. Step machines use `{ step, ordinal }` so the server can
 * enforce max-ordinal semantics (see server/routes/onboardingState.routes.js).
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from 'lib/supabase';
import { apiFetch } from '../apiClient';
import { DOM_EVENT_ATLAS_MISSION_CHANGE } from '../config/storageKeys';
import { FEATURE_COACH_STORAGE_PREFIX, ONBOARDING_STORAGE_PREFIX } from './onboardingState';
import { ordinalFor } from './stepOrdinals';
import { registerOnboardingBridge } from './onboardingStateBridge';

export type MissionStepValue = { step: string; ordinal?: number };
export type OnboardingStateSlice = Record<string, unknown>;
export type OnboardingScope = 'org' | 'org-product' | 'user';

interface OnboardingSnapshot {
  org: OnboardingStateSlice;
  orgProduct: OnboardingStateSlice;
  userOverride: OnboardingStateSlice;
}

const EMPTY_SNAPSHOT: OnboardingSnapshot = Object.freeze({
  org: {},
  orgProduct: {},
  userOverride: {},
});

const ATLAS_BUS_EVENT = DOM_EVENT_ATLAS_MISSION_CHANGE;

const REALTIME_DISABLED =
  typeof process !== 'undefined' && process.env?.REACT_APP_DISABLE_REALTIME === '1';

export interface OnboardingStateContextValue {
  /** True until the first GET resolves. First-paint reads hit the localStorage fallback. */
  isLoading: boolean;
  /** Merged view: userOverride wins, then orgProduct, then org. */
  getMerged: (key: string) => unknown;
  /** Returns the mission step payload from the merged view, or undefined. */
  getMissionStep: (machineKey: string) => MissionStepValue | undefined;
  /** Advance (or set) a step machine. `scope='user'` for personal restart. */
  setMissionStep: (machineKey: string, step: string, scope: OnboardingScope) => Promise<void>;
  /** Set a boolean flag (checkpoint / coach dismissal). Monotonic: never un-sets. */
  setFlag: (key: string, scope: OnboardingScope) => Promise<void>;
  /** Personal "Restart mission": wipes user overlay for current (org, product). */
  clearUserOverride: () => Promise<void>;
  /** Owner/admin reset of canonical state for the current scope. */
  resetCanonical: (scope?: 'org' | 'org-product') => Promise<void>;
  /** Force a re-GET (used after org/product switch, also window focus). */
  refresh: () => Promise<void>;
  /** Raw snapshot for consumers that need to inspect multiple keys at once. */
  snapshot: OnboardingSnapshot;
}

const OnboardingStateContext = createContext<OnboardingStateContextValue | null>(null);

// ---------------------------------------------------------------------------
// localStorage fallback — builds a best-effort orgProduct slice from legacy
// per-browser keys. Used for first paint and for the bootstrap PATCH.
// ---------------------------------------------------------------------------

/** All legacy localStorage keys, organised by target scope. */
interface BootstrapKeys {
  orgProduct: OnboardingStateSlice;
  org: OnboardingStateSlice;
}

function scanLegacyLocalStorage(orgId: string, productId: string | null): BootstrapKeys {
  const out: BootstrapKeys = { orgProduct: {}, org: {} };
  if (typeof window === 'undefined') return out;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const rawKey = localStorage.key(i);
      if (!rawKey) continue;
      const value = localStorage.getItem(rawKey);
      if (value == null) continue;

      // Step machines
      //   relix.onboarding.forecastTour.<orgId>.<productId>.v1 = <step>
      //   relix.onboarding.releasesTour.<orgId>.<productId>.v1 = <step>
      //   relix.onboarding.postUpdateJourney.<orgId>.<productId>.v2 = <step>
      if (
        productId &&
        rawKey.startsWith(`${ONBOARDING_STORAGE_PREFIX}.forecastTour.${orgId}.${productId}`)
      ) {
        out.orgProduct['mission.forecast'] = {
          step: value,
          ordinal: ordinalFor('mission.forecast', value) ?? undefined,
        };
        continue;
      }
      if (
        productId &&
        rawKey.startsWith(`${ONBOARDING_STORAGE_PREFIX}.releasesTour.${orgId}.${productId}`)
      ) {
        out.orgProduct['mission.releases'] = {
          step: value,
          ordinal: ordinalFor('mission.releases', value) ?? undefined,
        };
        continue;
      }
      if (
        productId &&
        rawKey.startsWith(`${ONBOARDING_STORAGE_PREFIX}.postUpdateJourney.${orgId}.${productId}`)
      ) {
        out.orgProduct['mission.postUpdateJourney'] = {
          step: value,
          ordinal: ordinalFor('mission.postUpdateJourney', value) ?? undefined,
        };
        continue;
      }

      // Feature coach dismissals
      //   relix.coach.<featureKey>.<productId>.v2 = '1'  (product-scoped)
      //   relix.coach.<featureKey>.suppressAll.v2 = '1'  (org-wide)
      //   relix.coach.<featureKey>.completed       = '1'  (legacy global)
      if (rawKey.startsWith(`${FEATURE_COACH_STORAGE_PREFIX}.`)) {
        const tail = rawKey.slice(FEATURE_COACH_STORAGE_PREFIX.length + 1);
        if (tail.endsWith('.suppressAll.v2') && value === '1') {
          const feature = tail.slice(0, -'.suppressAll.v2'.length);
          out.org[`coach.${feature}.suppressAll`] = true;
          continue;
        }
        if (productId && tail.endsWith(`.${productId}.v2`) && value === '1') {
          const feature = tail.slice(0, -`.${productId}.v2`.length);
          out.orgProduct[`coach.${feature}.completed`] = true;
          continue;
        }
        if (tail.endsWith('.completed') && value === '1') {
          const feature = tail.slice(0, -'.completed'.length);
          out.orgProduct[`coach.${feature}.completed`] = true;
          continue;
        }
      }
    }
  } catch {
    // ignore quota / security errors — bootstrap is best-effort
  }
  return out;
}

function bootstrapFlagKey(orgId: string, productId: string | null): string {
  return `${ONBOARDING_STORAGE_PREFIX}.bootstrapped.${orgId}.${productId ?? 'org'}.v1`;
}

function hasBootstrapped(orgId: string, productId: string | null): boolean {
  try {
    return localStorage.getItem(bootstrapFlagKey(orgId, productId)) === '1';
  } catch {
    return false;
  }
}

function markBootstrapped(orgId: string, productId: string | null): void {
  try {
    localStorage.setItem(bootstrapFlagKey(orgId, productId), '1');
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const OnboardingStateProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, currentOrg, currentProduct } = useAuth();
  const orgId = currentOrg?.id ?? null;
  const productId = currentProduct?.id ?? null;
  const userId = user?.id ?? null;

  const [snapshot, setSnapshot] = useState<OnboardingSnapshot>(() => {
    if (!orgId) return EMPTY_SNAPSHOT;
    const seeded = scanLegacyLocalStorage(orgId, productId);
    return {
      org: seeded.org,
      orgProduct: seeded.orgProduct,
      userOverride: {},
    };
  });
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(orgId));
  const fetchIdRef = useRef(0);

  const publishBus = useCallback(() => {
    try {
      window.dispatchEvent(new CustomEvent(ATLAS_BUS_EVENT));
    } catch {
      // ignore
    }
  }, []);

  // -------------------------------------------------------------------------
  // Network helpers
  // -------------------------------------------------------------------------

  const doPatch = useCallback(
    async (
      scope: OnboardingScope,
      patch: OnboardingStateSlice
    ): Promise<OnboardingStateSlice | null> => {
      if (!orgId) return null;
      const headers = new Headers({ 'Content-Type': 'application/json' });
      headers.set('X-Organization-Id', orgId);
      if (productId) headers.set('X-Product-Id', productId);
      try {
        const res = await apiFetch('/api/onboarding-state', {
          method: 'PATCH',
          headers,
          body: JSON.stringify({ scope, patch }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = (await res.json()) as { state?: OnboardingStateSlice };
        return data.state ?? null;
      } catch (err) {
        // Non-fatal: provider keeps the optimistic update, realtime / next
        // GET will reconcile. Surface to console for diagnostics.
        // eslint-disable-next-line no-console
        console.warn('[onboardingState] PATCH failed:', err);
        return null;
      }
    },
    [orgId, productId]
  );

  const bootstrapFromLocalStorage = useCallback(async (): Promise<void> => {
    if (!orgId) return;
    if (hasBootstrapped(orgId, productId)) return;
    const seeded = scanLegacyLocalStorage(orgId, productId);
    const tasks: Promise<unknown>[] = [];
    if (Object.keys(seeded.orgProduct).length > 0 && productId) {
      tasks.push(doPatch('org-product', seeded.orgProduct));
    }
    if (Object.keys(seeded.org).length > 0) {
      tasks.push(doPatch('org', seeded.org));
    }
    if (tasks.length === 0) {
      markBootstrapped(orgId, productId);
      return;
    }
    await Promise.all(tasks);
    markBootstrapped(orgId, productId);
  }, [orgId, productId, doPatch]);

  const refresh = useCallback(async (): Promise<void> => {
    if (!orgId) {
      setSnapshot(EMPTY_SNAPSHOT);
      setIsLoading(false);
      return;
    }
    const myFetchId = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      const headers = new Headers();
      headers.set('X-Organization-Id', orgId);
      if (productId) headers.set('X-Product-Id', productId);
      const res = await apiFetch('/api/onboarding-state', { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as OnboardingSnapshot;
      // Avoid stepping on a newer in-flight fetch (e.g. user switched product
      // while this request was running).
      if (fetchIdRef.current !== myFetchId) return;
      setSnapshot({
        org: data.org || {},
        orgProduct: data.orgProduct || {},
        userOverride: data.userOverride || {},
      });

      // First-time bootstrap (server returned empty orgProduct AND we have
      // localStorage signals from before the server-sync era).
      const emptyOrgProduct = !data.orgProduct || Object.keys(data.orgProduct).length === 0;
      const emptyOrg = !data.org || Object.keys(data.org).length === 0;
      if (emptyOrgProduct && emptyOrg) {
        await bootstrapFromLocalStorage();
        // Re-fetch after bootstrap so we hand the caller the canonical view.
        const headers2 = new Headers();
        headers2.set('X-Organization-Id', orgId);
        if (productId) headers2.set('X-Product-Id', productId);
        const res2 = await apiFetch('/api/onboarding-state', { headers: headers2 });
        if (res2.ok && fetchIdRef.current === myFetchId) {
          const data2 = (await res2.json()) as OnboardingSnapshot;
          setSnapshot({
            org: data2.org || {},
            orgProduct: data2.orgProduct || {},
            userOverride: data2.userOverride || {},
          });
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[onboardingState] GET failed:', err);
      // Leave whatever we had (localStorage seed on first run) so the UI
      // keeps working offline.
    } finally {
      if (fetchIdRef.current === myFetchId) setIsLoading(false);
    }
  }, [orgId, productId, bootstrapFromLocalStorage]);

  // Initial + org/product-change fetch.
  useEffect(() => {
    if (!orgId) {
      setSnapshot(EMPTY_SNAPSHOT);
      setIsLoading(false);
      return;
    }
    // Seed local cache immediately so first paint doesn't flash.
    const seeded = scanLegacyLocalStorage(orgId, productId);
    setSnapshot((prev) => ({
      org: Object.keys(prev.org).length ? prev.org : seeded.org,
      orgProduct: Object.keys(prev.orgProduct).length ? prev.orgProduct : seeded.orgProduct,
      userOverride: prev.userOverride,
    }));
    void refresh();
  }, [orgId, productId, refresh]);

  // Refetch on window focus (cheap way to catch realtime gaps).
  useEffect(() => {
    const onFocus = () => {
      void refresh();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  // Supabase Realtime subscription.
  useEffect(() => {
    if (REALTIME_DISABLED) return;
    if (!orgId || !userId) return;
    let channelA: RealtimeChannel | null = null;
    let channelB: RealtimeChannel | null = null;
    try {
      channelA = supabase
        .channel(`onboarding_state:${orgId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'onboarding_state',
            filter: `organization_id=eq.${orgId}`,
          },
          () => {
            // Delta-apply is tricky (we'd need to re-run mergePatch on the
            // client). Just refetch — it's cheap and keeps the client code
            // small. Bounce through a microtask so a burst of changes
            // coalesces into a single GET.
            void refresh();
          }
        )
        .subscribe();

      channelB = supabase
        .channel(`user_onboarding_override:${userId}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'user_onboarding_override',
            filter: `user_id=eq.${userId}`,
          },
          () => {
            void refresh();
          }
        )
        .subscribe();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[onboardingState] Realtime subscribe failed:', err);
    }
    return () => {
      try {
        if (channelA) supabase.removeChannel(channelA);
        if (channelB) supabase.removeChannel(channelB);
      } catch {
        // ignore
      }
    };
  }, [orgId, userId, refresh]);

  // -------------------------------------------------------------------------
  // Merged-view helpers
  // -------------------------------------------------------------------------

  const getMerged = useCallback(
    (key: string): unknown => {
      const { org, orgProduct, userOverride } = snapshot;
      if (userOverride && key in userOverride) return userOverride[key];
      if (orgProduct && key in orgProduct) return orgProduct[key];
      if (org && key in org) return org[key];
      return undefined;
    },
    [snapshot]
  );

  const getMissionStep = useCallback(
    (machineKey: string): MissionStepValue | undefined => {
      const v = getMerged(machineKey);
      if (v && typeof v === 'object' && 'step' in v) {
        return v as MissionStepValue;
      }
      if (typeof v === 'string') {
        // Legacy shape from localStorage seed.
        return { step: v, ordinal: ordinalFor(machineKey, v) ?? undefined };
      }
      return undefined;
    },
    [getMerged]
  );

  // -------------------------------------------------------------------------
  // Mutations — optimistic update + server PATCH + bus event.
  // -------------------------------------------------------------------------

  const setMissionStep = useCallback(
    async (machineKey: string, step: string, scope: OnboardingScope) => {
      const ordinal = ordinalFor(machineKey, step) ?? undefined;
      const payload: MissionStepValue = ordinal != null ? { step, ordinal } : { step };
      // Optimistic local apply.
      setSnapshot((prev) => {
        if (scope === 'user') {
          return { ...prev, userOverride: { ...prev.userOverride, [machineKey]: payload } };
        }
        if (scope === 'org-product') {
          return { ...prev, orgProduct: { ...prev.orgProduct, [machineKey]: payload } };
        }
        return { ...prev, org: { ...prev.org, [machineKey]: payload } };
      });
      publishBus();
      await doPatch(scope, { [machineKey]: payload });
    },
    [doPatch, publishBus]
  );

  const setFlag = useCallback(
    async (key: string, scope: OnboardingScope) => {
      setSnapshot((prev) => {
        if (scope === 'user') {
          return { ...prev, userOverride: { ...prev.userOverride, [key]: true } };
        }
        if (scope === 'org-product') {
          return { ...prev, orgProduct: { ...prev.orgProduct, [key]: true } };
        }
        return { ...prev, org: { ...prev.org, [key]: true } };
      });
      publishBus();
      await doPatch(scope, { [key]: true });
    },
    [doPatch, publishBus]
  );

  const clearUserOverride = useCallback(async () => {
    if (!orgId) return;
    setSnapshot((prev) => ({ ...prev, userOverride: {} }));
    publishBus();
    try {
      const headers = new Headers();
      headers.set('X-Organization-Id', orgId);
      if (productId) headers.set('X-Product-Id', productId);
      await apiFetch('/api/onboarding-state/user-override', { method: 'DELETE', headers });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[onboardingState] DELETE user-override failed:', err);
    }
  }, [orgId, productId, publishBus]);

  const resetCanonical = useCallback(
    async (scope: 'org' | 'org-product' = 'org-product') => {
      if (!orgId) return;
      const headers = new Headers();
      headers.set('X-Organization-Id', orgId);
      if (productId) headers.set('X-Product-Id', productId);
      try {
        await apiFetch(`/api/onboarding-state?scope=${encodeURIComponent(scope)}`, {
          method: 'DELETE',
          headers,
        });
        await refresh();
        publishBus();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[onboardingState] DELETE canonical failed:', err);
      }
    },
    [orgId, productId, refresh, publishBus]
  );

  const value = useMemo<OnboardingStateContextValue>(
    () => ({
      isLoading,
      snapshot,
      getMerged,
      getMissionStep,
      setMissionStep,
      setFlag,
      clearUserOverride,
      resetCanonical,
      refresh,
    }),
    [
      isLoading,
      snapshot,
      getMerged,
      getMissionStep,
      setMissionStep,
      setFlag,
      clearUserOverride,
      resetCanonical,
      refresh,
    ]
  );

  // Expose the async mutators to the imperative adapter (see
  // `src/shared/onboarding/onboardingStateBridge.ts`). This is what lets the
  // synchronous `advanceMissionStep` chain publish to the server without
  // needing hooks inside Atlas callbacks.
  useEffect(() => {
    const unsubscribe = registerOnboardingBridge({
      setMissionStep,
      setFlag,
      clearUserOverride,
      resetCanonical,
      getMissionStep,
    });
    return unsubscribe;
  }, [setMissionStep, setFlag, clearUserOverride, resetCanonical, getMissionStep]);

  return (
    <OnboardingStateContext.Provider value={value}>{children}</OnboardingStateContext.Provider>
  );
};

export function useOnboardingStateOptional(): OnboardingStateContextValue | null {
  return useContext(OnboardingStateContext);
}

/**
 * Hook variant that returns a stubbed value when the provider isn't mounted
 * (e.g. in storybook / test-renderer). Mutations become no-ops; reads return
 * undefined. Atlas code should tolerate this gracefully.
 */
export function useOnboardingState(): OnboardingStateContextValue {
  const v = useContext(OnboardingStateContext);
  if (v) return v;
  return {
    isLoading: false,
    snapshot: EMPTY_SNAPSHOT,
    getMerged: () => undefined,
    getMissionStep: () => undefined,
    setMissionStep: async () => {},
    setFlag: async () => {},
    clearUserOverride: async () => {},
    resetCanonical: async () => {},
    refresh: async () => {},
  };
}
